(() => {
  if (customElements.get('theme-popup')) return;

  const STORAGE_PREFIX = 'themePopup:';
  const DAY_MS = 24 * 60 * 60 * 1000;

  class ThemePopup extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.closeButton = this.querySelector('[data-popup-close]');
      this.conversionTargets = this.querySelectorAll('[data-popup-conversion]');
      this.conversionForms = this.querySelectorAll('form');

      if (!this.dialog || this.shouldSkip()) return;

      this.closeButton?.addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('click', this.handleBackdropClick);
      this.dialog.addEventListener('close', this.handleClose);

      this.conversionTargets.forEach((target) => {
        target.addEventListener('click', this.handleConversion);
      });

      this.conversionForms.forEach((form) => {
        form.addEventListener('submit', this.handleConversion);
      });

      this.armTrigger();
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      window.removeEventListener('scroll', this.handleScroll);

      this.dialog?.removeEventListener('click', this.handleBackdropClick);
      this.dialog?.removeEventListener('close', this.handleClose);

      this.conversionTargets?.forEach((target) => {
        target.removeEventListener('click', this.handleConversion);
      });

      this.conversionForms?.forEach((form) => {
        form.removeEventListener('submit', this.handleConversion);
      });
    }

    get popupId() {
      return this.dataset.popupId || 'default';
    }

    get oncePerSession() {
      return this.dataset.oncePerSession !== 'false';
    }

    get closeCooldownDays() {
      return Number(this.dataset.closeCooldownDays) || 0;
    }

    get clickCooldownDays() {
      return Number(this.dataset.clickCooldownDays) || this.closeCooldownDays;
    }

    get isMobileDisabled() {
      return this.dataset.enableMobile === 'false' && window.matchMedia('(max-width: 749px)').matches;
    }

    get sessionKey() {
      return `${STORAGE_PREFIX}sessionShown`;
    }

    get globalCooldownKey() {
      return `${STORAGE_PREFIX}globalCooldownUntil`;
    }

    get localCooldownKey() {
      return `${STORAGE_PREFIX}${this.popupId}:cooldownUntil`;
    }

    shouldSkip() {
      return this.isMobileDisabled || this.isCoolingDown() || this.wasShownThisSession();
    }

    armTrigger() {
      if (this.dataset.trigger === 'scroll') {
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        this.handleScroll();
        return;
      }

      const delay = Number(this.dataset.delay) || 60000;
      this.timer = window.setTimeout(() => this.showPopup(), delay);
    }

    handleScroll = () => {
      const scrollPercent = Number(this.dataset.scrollPercent) || 30;
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;

      const currentPercent = (window.scrollY / scrollable) * 100;
      if (currentPercent >= scrollPercent) {
        window.removeEventListener('scroll', this.handleScroll);
        this.showPopup();
      }
    };

    showPopup() {
      if (this.dialog.open || this.shouldSkip() || document.querySelector('dialog[open]')) return;

      try {
        this.dialog.showModal();
        this.markShownThisSession();
      } catch {
        this.dialog.setAttribute('open', '');
        this.markShownThisSession();
      }
    }

    handleBackdropClick = (event) => {
      if (event.target === this.dialog) this.dialog.close();
    };

    handleClose = () => {
      this.setCooldown(this.closeCooldownDays);
    };

    handleConversion = () => {
      this.setCooldown(this.clickCooldownDays);
    };

    wasShownThisSession() {
      if (!this.oncePerSession) return false;

      try {
        return window.sessionStorage.getItem(this.sessionKey) === 'true';
      } catch {
        return false;
      }
    }

    markShownThisSession() {
      if (!this.oncePerSession) return;

      try {
        window.sessionStorage.setItem(this.sessionKey, 'true');
      } catch {
        // Popups still work when session storage is unavailable.
      }
    }

    isCoolingDown() {
      const now = Date.now();
      return this.readTimestamp(this.globalCooldownKey) > now || this.readTimestamp(this.localCooldownKey) > now;
    }

    readTimestamp(key) {
      try {
        return Number(window.localStorage.getItem(key)) || 0;
      } catch {
        return 0;
      }
    }

    setCooldown(days) {
      if (!days || days < 0) return;

      const until = Date.now() + days * DAY_MS;
      this.writeMaxTimestamp(this.globalCooldownKey, until);
      this.writeMaxTimestamp(this.localCooldownKey, until);
    }

    writeMaxTimestamp(key, value) {
      try {
        const current = this.readTimestamp(key);
        window.localStorage.setItem(key, String(Math.max(current, value)));
      } catch {
        // Popups still work when local storage is unavailable.
      }
    }
  }

  customElements.define('theme-popup', ThemePopup);
})();
